import { createContext, Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Maximize2, MapPin, Minimize2, RotateCcw, RotateCw, ZoomIn, ZoomOut, Crosshair } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DEFAULT_CAMERA,
  canDrop,
  focusCamera,
  layoutFloor,
  screenToPlane,
  type IsoCamera,
  type IsoRoomBox,
} from '@/lib/floorIsometric'
import type { LocationRoom } from '@/api/facilityApi'
import { SHAPE_SIZE, layoutUnits, shapeOf, snapSpot } from '@/lib/unitShapes'
import { UnitModel } from './UnitModels'
import { cutouts, fitsInside, floorRects, toOutline, wallRuns, type RoomOutline, type WallRun } from '@/lib/roomOutline'

/** Pixels per layout cell, and how tall walls and units stand. */
const CELL = 30
const WALL = 20
/** The vertical gap between storeys in the building view. */
const STOREY = 120
const SLAB = 8

/**
 * The camera lives in CSS custom properties on the canvas, not in React
 * state: dragging, zooming and turning change five numbers on one element and
 * the browser moves the scene, with no re-render of rooms, walls or units.
 * Code that needs the numbers (turning a drag into floor cells) reads them
 * from the ref this context hands out.
 */
const CameraContext = createContext<{ current: IsoCamera }>({ current: DEFAULT_CAMERA })

/** The plane follows the camera through the custom properties. */
const PLANE_TRANSFORM =
  'translate(calc(var(--iso-pan-x) * 1px), calc(var(--iso-pan-y) * 1px)) scale(var(--iso-zoom)) rotateX(calc(var(--iso-tilt) * 1deg)) rotateZ(calc(var(--iso-turn) * 1deg))'
/** Undoes the camera's turn and tilt, so a label stands up facing the viewer. */
const FACE_VIEWER = 'rotateZ(calc(var(--iso-turn) * -1deg)) rotateX(calc(var(--iso-tilt) * -1deg))'

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

function writeCamera(el: HTMLElement | null, c: IsoCamera) {
  if (!el) return
  el.style.setProperty('--iso-tilt', String(c.tilt))
  el.style.setProperty('--iso-turn', String(c.turn))
  el.style.setProperty('--iso-zoom', String(c.zoom))
  el.style.setProperty('--iso-pan-x', String(c.panX))
  el.style.setProperty('--iso-pan-y', String(c.panY))
}

/**
 * The 3D canvas (z8nrz7fw25): drag to turn the view, Shift-drag or
 * right-drag to move it, scroll to zoom, and the buttons do the same plus a
 * reset and full screen. Rooms and floors handle their own drags and stop
 * them here, so moving a room never turns the camera. Pointer and wheel
 * input is applied at most once a frame.
 */
function IsoViewport({
  height,
  label,
  className,
  focus,
  children,
}: {
  height: number
  label: string
  className?: string
  /** A point to fly to, relative to the plane's centre; a new `key` flies again (a room clicked). */
  focus?: { x: number; y: number; z: number; key: string } | null
  children: ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  const camera = useRef<IsoCamera>(DEFAULT_CAMERA)
  const frame = useRef<number | null>(null)
  const drag = useRef<{ x: number; y: number; pan: boolean } | null>(null)
  const [grabbing, setGrabbing] = useState(false)
  const [full, setFull] = useState(false)

  // One write per frame, however many events arrived in it.
  const update = (next: (c: IsoCamera) => IsoCamera) => {
    camera.current = next(camera.current)
    if (frame.current !== null) return
    frame.current = requestAnimationFrame(() => {
      frame.current = null
      writeCamera(box.current, camera.current)
    })
  }

  // The first paint already has the camera.
  useLayoutEffect(() => {
    writeCamera(box.current, camera.current)
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
    }
  }, [])

  // Clicking a room brings it to the middle, closer, from the angle already set.
  const focusKey = focus?.key ?? null
  useEffect(() => {
    if (focus) update((c) => focusCamera(c, focus))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey])

  // Scroll zooms the canvas, not the page: a passive listener could not stop that.
  useEffect(() => {
    const el = box.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      update((c) => ({ ...c, zoom: clamp(c.zoom * Math.exp(-e.deltaY * 0.0015), 0.35, 2.8) }))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === box.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const zoomBy = (f: number) => update((c) => ({ ...c, zoom: clamp(c.zoom * f, 0.35, 2.8) }))
  const turnBy = (deg: number) => update((c) => ({ ...c, turn: c.turn + deg }))

  return (
    <div
      ref={box}
      className={cn(
        'relative flex w-full touch-none select-none items-center justify-center overflow-hidden rounded-xl bg-gradient-to-b from-slate-50 to-slate-100',
        grabbing ? 'cursor-grabbing' : 'cursor-grab',
        full && 'h-screen rounded-none',
        className,
      )}
      style={{ height: full ? undefined : height, perspective: 2200 }}
      role='group'
      aria-label={label}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, y: e.clientY, pan: e.shiftKey || e.button === 2 || e.button === 1 }
        e.currentTarget.setPointerCapture(e.pointerId)
        setGrabbing(true)
      }}
      onPointerMove={(e) => {
        const d = drag.current
        if (!d) return
        const dx = e.clientX - d.x
        const dy = e.clientY - d.y
        drag.current = { ...d, x: e.clientX, y: e.clientY }
        update((c) =>
          d.pan
            ? { ...c, panX: c.panX + dx, panY: c.panY + dy }
            : { ...c, turn: c.turn + dx * 0.4, tilt: clamp(c.tilt - dy * 0.3, 15, 80) },
        )
      }}
      onPointerUp={() => {
        drag.current = null
        setGrabbing(false)
      }}
    >
      <CameraContext.Provider value={camera}>{children}</CameraContext.Provider>

      <div className='absolute right-3 top-3 flex flex-col gap-1' onPointerDown={(e) => e.stopPropagation()}>
        {(
          [
            ['Zoom in', ZoomIn, () => zoomBy(1.2)],
            ['Zoom out', ZoomOut, () => zoomBy(1 / 1.2)],
            ['Turn left', RotateCcw, () => turnBy(-15)],
            ['Turn right', RotateCw, () => turnBy(15)],
            ['Reset view', Crosshair, () => update(() => DEFAULT_CAMERA)],
            [
              full ? 'Leave full screen' : 'Full screen',
              full ? Minimize2 : Maximize2,
              () => (full ? void document.exitFullscreen() : void box.current?.requestFullscreen()),
            ],
          ] as const
        ).map(([title, Icon, onClick]) => (
          <button
            key={title}
            type='button'
            title={title}
            aria-label={title}
            onClick={onClick}
            className='flex h-8 w-8 items-center justify-center rounded-lg border bg-white/95 text-slate-600 shadow-sm hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          >
            <Icon className='h-4 w-4' />
          </button>
        ))}
      </div>
      <p className='pointer-events-none absolute bottom-3 right-3 rounded-md bg-white/80 px-2 py-1 text-[11px] text-slate-500'>
        Drag to turn · Shift-drag to move · Scroll to zoom
      </p>
    </div>
  )
}

/**
 * One floor in 3D: the slab, each room a walled block standing on it, the
 * units in a room as small boxes along its back wall. The selected room is lit
 * and pinned with its name. With `onMoveRoom`, rooms are dragged to a new
 * spot on the floor's grid.
 *
 * Pure CSS 3D transforms, no 3D library. Rooms not placed by hand are laid out
 * by `layoutFloor`, sized from their area — a generated view, not a floor plan.
 */
export function FloorIsometric({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onMoveRoom,
  highlightUnitId,
  className,
}: {
  rooms: LocationRoom[]
  selectedRoomId: string | null
  onSelectRoom: (roomId: string) => void
  /** Drag rooms around the floor; omitted, rooms only select. */
  onMoveRoom?: (roomId: string, x: number, y: number) => void
  /** The unit being looked at — the storage unit whose page this was opened from. Drawn apart and tagged. */
  highlightUnitId?: string | null
  className?: string
}) {
  // Laid out once per change of the rooms, not on every render.
  const { boxes, width, depth } = useMemo(() => layoutFloor(rooms.map(toInput)), [rooms])
  const byId = new Map(rooms.map((r) => [r.id, r]))
  const w = width * CELL
  const d = depth * CELL
  const height = Math.round((w * 0.62 + d * 0.8) * Math.cos((DEFAULT_CAMERA.tilt * Math.PI) / 180)) + 110
  const focusBox = boxes.find((b) => b.id === selectedRoomId)

  return (
    <IsoViewport height={height} label='Floor in 3D' className={className} focus={focusOn(focusBox, w, d, 0)}>
      <Plane width={w} depth={d}>
        <Slab width={w} depth={d} chosen />
        {boxes.map((box) => {
          const room = byId.get(box.id)
          return room ? (
            <RoomBlock
              key={box.id}
              box={box}
              others={boxes}
              room={room}
              selected={room.id === selectedRoomId}
              onSelect={() => onSelectRoom(room.id)}
              onMove={onMoveRoom ? (x, y) => onMoveRoom(room.id, x, y) : undefined}
              highlightUnitId={highlightUnitId ?? null}
            />
          ) : null
        })}
      </Plane>
    </IsoViewport>
  )
}

export interface IsoFloor {
  id: string
  name: string
  level: number
  rooms: LocationRoom[]
}

/**
 * The whole building in 3D: every floor a slab stacked at its level, ground
 * floor at the bottom, with its rooms standing on it. The chosen floor is drawn
 * in full — colours, units, names — and the others are outlines to see
 * through. A floor's tag selects it; with `onMoveFloor`, dragging the tag up or
 * down moves the floor in the building. Rooms on the chosen floor drag too.
 */
export function BuildingIsometric({
  floors,
  selectedFloorId,
  onSelectFloor,
  onMoveFloor,
  selectedRoomId,
  onSelectRoom,
  onMoveRoom,
  highlightUnitId,
  className,
}: {
  floors: IsoFloor[]
  selectedFloorId: string | null
  onSelectFloor: (floorId: string) => void
  /** Drag a floor's tag up or down by storeys; omitted, tags only select. */
  onMoveFloor?: (floorId: string, steps: number) => void
  selectedRoomId: string | null
  onSelectRoom: (roomId: string) => void
  onMoveRoom?: (roomId: string, x: number, y: number) => void
  highlightUnitId?: string | null
  className?: string
}) {
  const layouts = useMemo(
    () =>
      [...floors].sort((a, b) => a.level - b.level).map((f) => ({ floor: f, ...layoutFloor(f.rooms.map(toInput)) })),
    [floors],
  )
  // One footprint for every storey, so the stack lines up like a building.
  const w = Math.max(...layouts.map((l) => l.width), 10) * CELL
  const d = Math.max(...layouts.map((l) => l.depth), 6) * CELL
  const lift = STOREY * Math.sin((DEFAULT_CAMERA.tilt * Math.PI) / 180)
  const height = Math.round((w * 0.62 + d * 0.8) * Math.cos((DEFAULT_CAMERA.tilt * Math.PI) / 180) + (layouts.length - 1) * lift) + 140
  const chosenIndex = layouts.findIndex((l) => l.floor.id === selectedFloorId)
  const focusBox = chosenIndex >= 0 ? layouts[chosenIndex].boxes.find((b) => b.id === selectedRoomId) : undefined

  return (
    <IsoViewport
      height={height}
      label={`Building in 3D, ${floors.length} floors`}
      className={className}
      focus={focusOn(focusBox, w, d, chosenIndex * STOREY)}
    >
      <Plane width={w} depth={d}>
        {layouts.map(({ floor, boxes }, i) => (
          <Storey
            key={floor.id}
            floor={floor}
            boxes={boxes}
            index={i}
            width={w}
            depth={d}
            chosen={floor.id === selectedFloorId}
            onSelectFloor={() => onSelectFloor(floor.id)}
            onMoveFloor={onMoveFloor ? (steps) => onMoveFloor(floor.id, steps) : undefined}
            selectedRoomId={selectedRoomId}
            onSelectRoom={onSelectRoom}
            onMoveRoom={onMoveRoom}
            highlightUnitId={highlightUnitId ?? null}
          />
        ))}
      </Plane>
    </IsoViewport>
  )
}

/** A room's centre, relative to the plane's centre, as a point the camera flies to. */
function focusOn(box: IsoRoomBox | undefined, planeW: number, planeD: number, z: number) {
  if (!box) return null
  return {
    x: (box.x + box.w / 2) * CELL - planeW / 2,
    y: (box.y + box.d / 2) * CELL - planeD / 2,
    z: z + WALL,
    key: `${box.id}:${box.x}:${box.y}:${z}`,
  }
}

/**
 * A room's floor and walls for any outline (z8nrz7fw25): the floor as its
 * pieces, a wall along every edge with no floor beyond it. Each side can stand
 * at its own height — the room view keeps the front and right low so the
 * inside shows.
 */
function RoomShell({
  outline,
  w,
  d,
  cell,
  floor,
  edge,
  heights,
  colors,
  tile,
}: {
  outline: RoomOutline
  w: number
  d: number
  cell: number
  floor: string
  edge: string
  heights: Record<WallRun['side'], number>
  colors: Record<WallRun['side'], string>
  /** A grid on the floor, in pixels. */
  tile?: number
}) {
  return (
    <>
      {floorRects(outline, w, d).map((r) => (
        <div
          key={`${r.x}:${r.y}`}
          style={{
            position: 'absolute',
            left: r.x * cell,
            top: r.y * cell,
            width: r.w * cell,
            height: r.d * cell,
            backgroundColor: floor,
            backgroundImage: tile
              ? 'linear-gradient(rgb(226 232 240) 1px, transparent 1px), linear-gradient(90deg, rgb(226 232 240) 1px, transparent 1px)'
              : undefined,
            backgroundSize: tile ? `${tile}px ${tile}px` : undefined,
            pointerEvents: 'none',
          }}
        />
      ))}
      {wallRuns(outline, w, d).map((run) => {
        const h = heights[run.side]
        const along = run.side === 'back' || run.side === 'front'
        return (
          <div
            key={`${run.side}:${run.x}:${run.y}`}
            style={{
              position: 'absolute',
              left: run.x * cell,
              top: run.y * cell,
              width: along ? run.length * cell : h,
              height: along ? h : run.length * cell,
              transformOrigin: along ? 'top' : 'left',
              transform: along ? 'rotateX(90deg)' : 'rotateY(-90deg)',
              backgroundColor: colors[run.side],
              border: `1px solid ${edge}`,
              pointerEvents: 'none',
            }}
          />
        )
      })}
    </>
  )
}

/** A clip-path that keeps a room's click area to its floor, cut-away excluded. */
function clipFor(outline: RoomOutline, w: number, d: number, cell: number): string | undefined {
  if (outline === 'rect') return undefined
  // The bounding box minus each cut-away, as an even-odd path in pixels.
  const rect = (r: { x: number; y: number; w: number; d: number }) =>
    `M ${r.x * cell} ${r.y * cell} H ${(r.x + r.w) * cell} V ${(r.y + r.d) * cell} H ${r.x * cell} Z`
  return `path(evenodd, '${[rect({ x: 0, y: 0, w, d }), ...cutouts(outline, w, d).map(rect)].join(' ')}')`
}

/** Pixels per cell inside a room: big enough to see what each unit is. */
const ROOM_CELL = 64
const ROOM_WALL = 84
const ROOM_SILL = 14

/**
 * One room, large, as a cut-away (z8nrz7fw25): the back and left walls full
 * height, the front and right low so the inside shows. Each unit stands where
 * it is, drawn as what it is, with its name over it — so finding a freezer in
 * a room is looking at the room. With `onMoveUnit`, units are dragged to a
 * new spot (half cells); clicking one selects it.
 */
export function RoomIsometric({
  room,
  width,
  depth,
  selectedUnitId,
  onSelectUnit,
  onMoveUnit,
  highlightUnitId,
  className,
}: {
  room: LocationRoom
  /** The room's size in cells: as set by hand, else as laid out on its floor. */
  width: number
  depth: number
  selectedUnitId: string | null
  onSelectUnit: (unitId: string) => void
  onMoveUnit?: (unitId: string, x: number, y: number) => void
  highlightUnitId?: string | null
  className?: string
}) {
  const w = width * ROOM_CELL
  const d = depth * ROOM_CELL
  const outline = toOutline(room.outline)
  // In an L, U or T room a unit must stand on the floor, not in the cut-away.
  const fits = useMemo(
    () => (b: { x: number; y: number; w: number; d: number }) => fitsInside(b, outline, width, depth),
    [outline, width, depth],
  )
  const spots = useMemo(
    () =>
      layoutUnits(
        room.units.map((u) => ({ id: u.id, shape: shapeOf(u), spot: u.spot ?? null })),
        width,
        depth,
        outline === 'rect' ? undefined : fits,
      ),
    [room.units, width, depth, outline, fits],
  )
  const height = Math.round((w * 0.62 + d * 0.8) * Math.cos((DEFAULT_CAMERA.tilt * Math.PI) / 180)) + ROOM_WALL + 120
  const focused = spots.find((s) => s.id === selectedUnitId)

  return (
    <IsoViewport
      height={Math.min(height, 720)}
      label={`${room.label} in 3D`}
      className={className}
      focus={focused ? { x: (focused.x + focused.w / 2) * ROOM_CELL - w / 2, y: (focused.y + focused.d / 2) * ROOM_CELL - d / 2, z: 30, key: `${focused.id}:${focused.x}:${focused.y}` } : null}
    >
      <Plane width={w} depth={d}>
        {/* The floor, tiled by half cell, and a cut-away of its walls: full at
            the back and left, low at the front and right, whatever the outline. */}
        <RoomShell
          outline={outline}
          w={width}
          d={depth}
          cell={ROOM_CELL}
          floor='rgb(248 250 252)'
          edge='rgb(203 213 225)'
          tile={ROOM_CELL / 2}
          heights={{ back: ROOM_WALL, left: ROOM_WALL, front: ROOM_SILL, right: ROOM_SILL }}
          colors={{ back: 'rgb(241 245 249)', left: 'rgb(226 232 240)', front: 'rgb(203 213 225)', right: 'rgb(203 213 225)' }}
        />

        {spots.map((spot, i) => (
          <PlacedUnitModel
            key={spot.id}
            unit={room.units[i]}
            spot={spot}
            room={{ w: width, d: depth }}
            fits={fits}
            selected={spot.id === selectedUnitId}
            highlight={spot.id === highlightUnitId}
            onSelect={() => onSelectUnit(spot.id)}
            onMove={onMoveUnit ? (x, y) => onMoveUnit(spot.id, x, y) : undefined}
          />
        ))}
      </Plane>
    </IsoViewport>
  )
}

/** One unit in the room view: its model, its name tag, and a footprint to click or drag. */
function PlacedUnitModel({
  unit,
  spot,
  room,
  fits,
  selected,
  highlight,
  onSelect,
  onMove,
}: {
  unit: LocationRoom['units'][number]
  spot: { x: number; y: number; w: number; d: number }
  room: { w: number; d: number }
  /** Whether a footprint stands on the room's floor (its outline's cut-away is not floor). */
  fits: (box: { x: number; y: number; w: number; d: number }) => boolean
  selected: boolean
  highlight: boolean
  onSelect: () => void
  onMove?: (x: number, y: number) => void
}) {
  const camera = useContext(CameraContext)
  const start = useRef<{ x: number; y: number } | null>(null)
  const [at, setAt] = useState<{ x: number; y: number } | null>(null)
  const shape = shapeOf(unit)
  const pos = at ?? { x: spot.x, y: spot.y }
  const moved = at !== null && (at.x !== spot.x || at.y !== spot.y)
  const ok = !moved || fits({ ...spot, x: at!.x, y: at!.y })
  const tone = highlight ? 'focus' : selected || moved ? 'picked' : unit.status === 'OUT_OF_SERVICE' ? 'down' : 'normal'

  return (
    <>
      <UnitModel shape={shape} cell={ROOM_CELL} tone={tone} x={pos.x * ROOM_CELL} y={pos.y * ROOM_CELL} />
      {/* The footprint is what is grabbed: the model itself lets clicks through. */}
      <button
        type='button'
        aria-pressed={selected}
        aria-label={`${unit.label}, ${unit.typeName}${onMove ? '. Drag to move it in the room.' : ''}`}
        className={cn(
          'absolute rounded-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary',
          onMove ? 'cursor-move' : 'cursor-pointer',
          selected ? 'ring-2 ring-emerald-500' : '',
        )}
        style={{
          left: pos.x * ROOM_CELL - 4,
          top: pos.y * ROOM_CELL - 4,
          width: spot.w * ROOM_CELL + 8,
          height: spot.d * ROOM_CELL + 8,
          backgroundColor: moved ? (ok ? 'rgb(16 185 129 / 0.15)' : 'rgb(239 68 68 / 0.25)') : 'transparent',
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
          start.current = { x: e.clientX, y: e.clientY }
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!start.current || !onMove) return
          const p = screenToPlane(e.clientX - start.current.x, e.clientY - start.current.y, camera.current)
          setAt(snapSpot(spot.x + p.x / ROOM_CELL, spot.y + p.y / ROOM_CELL, spot, room))
        }}
        onPointerUp={() => {
          const to = at
          start.current = null
          setAt(null)
          if (to && (to.x !== spot.x || to.y !== spot.y)) {
            // Dropped in the cut-away of an L, U or T room: it stays where it was.
            if (fits({ ...spot, x: to.x, y: to.y })) onMove?.(to.x, to.y)
          } else onSelect()
        }}
      />
      <div
        className='pointer-events-none absolute'
        style={{
          left: (pos.x + spot.w / 2) * ROOM_CELL,
          top: (pos.y + spot.d / 2) * ROOM_CELL,
          transformStyle: 'preserve-3d',
          transform: `translateZ(${SHAPE_SIZE[shape].h * ROOM_CELL * 0.5 + 14}px) ${FACE_VIEWER}`,
        }}
      >
        <span
          className={cn(
            'block -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold shadow',
            highlight ? 'bg-primary text-primary-foreground' : selected ? 'bg-emerald-600 text-white' : 'border bg-white/95 text-slate-800',
          )}
        >
          {unit.label}
        </span>
      </div>
    </>
  )
}

function toInput(r: LocationRoom) {
  return { id: r.id, areaSqm: r.areaSqm ?? null, unitCount: r.units.length, plan: r.plan ?? null, size: r.size ?? null }
}

/** The tilted, turned stage everything stands on, following the camera. */
function Plane({ width, depth, children }: { width: number; depth: number; children: ReactNode }) {
  return (
    <div className='relative' style={{ width, height: depth, transformStyle: 'preserve-3d', transform: PLANE_TRANSFORM }}>
      {children}
    </div>
  )
}

/** A floor slab: the grid on top and its thickness on the two edges facing the viewer. */
function Slab({ width, depth, chosen }: { width: number; depth: number; chosen: boolean }) {
  return (
    <>
      <div
        className='absolute inset-0 rounded-md'
        style={{
          backgroundColor: chosen ? 'rgb(248 250 252)' : 'rgb(241 245 249 / 0.35)',
          backgroundImage: chosen
            ? 'linear-gradient(rgb(226 232 240) 1px, transparent 1px), linear-gradient(90deg, rgb(226 232 240) 1px, transparent 1px)'
            : undefined,
          backgroundSize: `${CELL}px ${CELL}px`,
          border: `2px solid ${chosen ? 'rgb(59 130 246 / 0.6)' : 'rgb(148 163 184 / 0.6)'}`,
          boxShadow: chosen ? '0 30px 60px -20px rgb(15 23 42 / 0.3)' : undefined,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: depth,
          width,
          height: SLAB,
          transformOrigin: 'top',
          transform: 'rotateX(-90deg)',
          backgroundColor: chosen ? 'rgb(147 197 253)' : 'rgb(203 213 225 / 0.6)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: width,
          top: 0,
          width: SLAB,
          height: depth,
          transformOrigin: 'left',
          transform: 'rotateY(90deg)',
          backgroundColor: chosen ? 'rgb(96 165 250)' : 'rgb(148 163 184 / 0.5)',
          pointerEvents: 'none',
        }}
      />
    </>
  )
}

/** One storey of the building view, its tag draggable up and down the stack. */
function Storey({
  floor,
  boxes,
  index,
  width,
  depth,
  chosen,
  onSelectFloor,
  onMoveFloor,
  selectedRoomId,
  onSelectRoom,
  onMoveRoom,
  highlightUnitId,
}: {
  floor: IsoFloor
  boxes: IsoRoomBox[]
  index: number
  width: number
  depth: number
  chosen: boolean
  onSelectFloor: () => void
  onMoveFloor?: (steps: number) => void
  selectedRoomId: string | null
  onSelectRoom: (roomId: string) => void
  onMoveRoom?: (roomId: string, x: number, y: number) => void
  highlightUnitId: string | null
}) {
  const camera = useContext(CameraContext)
  const [lift, setLift] = useState(0)
  const start = useRef<number | null>(null)
  const byId = new Map(floor.rooms.map((r) => [r.id, r]))
  // How far one storey is on screen, to turn a vertical drag into storeys.
  const storeyPx = STOREY * Math.sin((camera.current.tilt * Math.PI) / 180) * camera.current.zoom
  const steps = Math.round(lift / Math.max(20, storeyPx))

  return (
    <div
      className='absolute inset-0'
      style={{ transformStyle: 'preserve-3d', transform: `translateZ(${index * STOREY + (start.current !== null ? lift / Math.max(0.2, camera.current.zoom) : 0)}px)` }}
    >
      <Slab width={width} depth={depth} chosen={chosen} />
      {boxes.map((box) => {
        const room = byId.get(box.id)
        return room ? (
          <RoomBlock
            key={box.id}
            box={box}
            others={boxes}
            room={room}
            selected={chosen && room.id === selectedRoomId}
            onSelect={() => onSelectRoom(room.id)}
            onMove={chosen && onMoveRoom ? (x, y) => onMoveRoom(room.id, x, y) : undefined}
            highlightUnitId={chosen ? highlightUnitId : null}
            ghost={!chosen}
          />
        ) : null
      })}

      {/* The floor's tag, at its front-left corner, facing the viewer. */}
      <div className='absolute' style={{ left: 0, top: depth, transformStyle: 'preserve-3d', transform: `translateZ(4px) ${FACE_VIEWER}` }}>
        <button
          type='button'
          aria-pressed={chosen}
          title={onMoveFloor ? 'Click to choose this floor; drag up or down to move it in the building' : undefined}
          onPointerDown={(e) => {
            e.stopPropagation()
            start.current = e.clientY
            e.currentTarget.setPointerCapture(e.pointerId)
          }}
          onPointerMove={(e) => {
            if (start.current === null || !onMoveFloor) return
            setLift(start.current - e.clientY)
          }}
          onPointerUp={() => {
            const moved = onMoveFloor && steps !== 0
            start.current = null
            setLift(0)
            if (moved) onMoveFloor(steps)
            else onSelectFloor()
          }}
          className={cn(
            'flex -translate-x-full -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            onMoveFloor ? 'cursor-ns-resize' : 'cursor-pointer',
            chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700 hover:border-primary',
          )}
        >
          <span className='font-bold tabular-nums'>{floor.level}</span>
          <span>{floor.name}</span>
          <span className={cn('tabular-nums', chosen ? 'text-primary-foreground/80' : 'text-slate-400')}>
            {steps !== 0 ? `${steps > 0 ? '↑' : '↓'} ${Math.abs(steps)}` : `${floor.rooms.length} ${floor.rooms.length === 1 ? 'room' : 'rooms'}`}
          </span>
        </button>
      </div>
    </div>
  )
}

function RoomBlock({
  box,
  others,
  room,
  selected,
  onSelect,
  onMove,
  highlightUnitId,
  ghost = false,
}: {
  box: IsoRoomBox
  others: IsoRoomBox[]
  room: LocationRoom
  selected: boolean
  onSelect: () => void
  /** Drag to a new spot, in cells; omitted, a click only selects. */
  onMove?: (x: number, y: number) => void
  highlightUnitId: string | null
  /** On a floor that is not the chosen one in the building view: an outline to see through, not to click. */
  ghost?: boolean
}) {
  const camera = useContext(CameraContext)
  const start = useRef<{ x: number; y: number } | null>(null)
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null)
  const dragging = offset !== null && (offset.x !== 0 || offset.y !== 0)
  const fits = !dragging || canDrop(box, box.x + offset!.x, box.y + offset!.y, others)

  const down = room.status === 'OUT_OF_SERVICE'
  const floor = ghost
    ? 'rgb(255 255 255 / 0.25)'
    : dragging && !fits
      ? 'rgb(254 202 202)'
      : selected
        ? 'rgb(191 219 254)'
        : down
          ? 'rgb(254 243 199)'
          : room.units.length
            ? 'rgb(204 251 241)'
            : 'rgb(255 255 255)'
  const wall = ghost ? 'rgb(226 232 240 / 0.45)' : selected || dragging ? 'rgb(147 197 253)' : 'rgb(241 245 249)'
  const wallSide = ghost ? 'rgb(203 213 225 / 0.45)' : selected || dragging ? 'rgb(96 165 250)' : 'rgb(226 232 240)'
  const edge = dragging && !fits ? 'rgb(239 68 68)' : selected ? 'rgb(59 130 246)' : 'rgb(203 213 225)'
  const bw = box.w * CELL
  const bd = box.d * CELL
  const x = (box.x + (offset?.x ?? 0)) * CELL
  const y = (box.y + (offset?.y ?? 0)) * CELL
  const outline = toOutline(room.outline)

  return (
    <div style={{ position: 'absolute', left: x, top: y, width: bw, height: bd, transformStyle: 'preserve-3d', transform: dragging ? 'translateZ(6px)' : undefined }}>
      <RoomShell
        outline={outline}
        w={box.w}
        d={box.d}
        cell={CELL}
        floor={floor}
        edge={edge}
        heights={{ back: WALL, left: WALL, front: WALL, right: WALL }}
        colors={{ back: wall, left: wall, front: wallSide, right: wallSide }}
      />
      {/* What is clicked and dragged: shaped like the room, so its cut-away is not part of it. */}
      <button
        type='button'
        aria-pressed={selected}
        aria-label={`${room.label}${room.name ? `, ${room.name}` : ''}${onMove ? '. Drag to move it on the floor.' : ''}`}
        className={cn(
          'absolute inset-0 rounded-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary',
          onMove ? 'cursor-move' : 'cursor-pointer',
        )}
        style={{ pointerEvents: ghost ? 'none' : undefined, backgroundColor: 'transparent', clipPath: clipFor(outline, box.w, box.d, CELL) }}
        onPointerDown={(e) => {
          e.stopPropagation()
          start.current = { x: e.clientX, y: e.clientY }
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!start.current || !onMove) return
          const p = screenToPlane(e.clientX - start.current.x, e.clientY - start.current.y, camera.current)
          setOffset({ x: Math.round(p.x / CELL), y: Math.round(p.y / CELL) })
        }}
        onPointerUp={() => {
          const moved = dragging
          const to = offset
          start.current = null
          setOffset(null)
          if (moved && to && fits) onMove?.(box.x + to.x, box.y + to.y)
          else if (!moved) onSelect()
        }}
      />
      {!ghost &&
        layoutUnits(
          room.units.map((u) => ({ id: u.id, shape: shapeOf(u), spot: u.spot ?? null })),
          box.w,
          box.d,
          outline === 'rect' ? undefined : (b) => fitsInside(b, outline, box.w, box.d),
        ).map((spot, i) => {
          const unit = room.units[i]
          const focus = unit.id === highlightUnitId
          const shape = shapeOf(unit)
          return (
            <Fragment key={unit.id}>
              <UnitModel
                shape={shape}
                cell={CELL}
                tone={focus ? 'focus' : unit.status === 'OUT_OF_SERVICE' ? 'down' : selected ? 'lit' : 'normal'}
                x={spot.x * CELL}
                y={spot.y * CELL}
              />
              {focus && (
                <div
                  className='pointer-events-none absolute'
                  style={{
                    left: (spot.x + spot.w / 2) * CELL,
                    top: (spot.y + spot.d / 2) * CELL,
                    transformStyle: 'preserve-3d',
                    transform: `translateZ(${SHAPE_SIZE[shape].h * CELL * 0.5 + 8}px) ${FACE_VIEWER}`,
                  }}
                >
                  <span className='block -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground shadow'>
                    {unit.label}
                  </span>
                </div>
              )}
            </Fragment>
          )
        })}

      {/* The label stands up and faces the viewer, whatever the camera. Not on a see-through floor. */}
      {!ghost && (
        <div
          className='pointer-events-none absolute'
          style={{ left: bw / 2, top: bd / 2, transformStyle: 'preserve-3d', transform: `translateZ(${WALL + 26}px) ${FACE_VIEWER}` }}
        >
          <div
            className={cn(
              'flex -translate-x-1/2 -translate-y-full items-center gap-1.5 whitespace-nowrap rounded-xl border bg-white/95 px-2.5 py-1.5 shadow-md',
              selected ? 'border-primary' : 'border-slate-200',
            )}
          >
            {selected && <MapPin className='h-4 w-4 shrink-0 text-primary' />}
            <span className='leading-tight'>
              <span className='block text-xs font-semibold text-slate-900'>{room.label}</span>
              {room.name && <span className='block text-[10px] text-slate-500'>{room.name}</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

